import { computed, reactive, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { LAIS_IMOVEIS_PERMISSIONS } from 'dashboard/routes/dashboard/laisRouteHelpers';
import { HIGHLIGHT_STATUS_META } from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  formatDateTime,
  toDatetimeLocalValue,
} from '../utils/imovelShowFormatters';

/**
 * Agendamento de destaque/vitrine do imóvel com confirmação explícita.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {Function} options.refreshImovel
 * @param {Function} options.trackListingAction
 */
export function useImovelHighlight({
  imovel,
  refreshImovel,
  trackListingAction,
}) {
  const { t } = useI18n();
  const { checkPermissions } = usePolicy();

  const canManageHighlight = computed(() =>
    checkPermissions(LAIS_IMOVEIS_PERMISSIONS)
  );

  const highlightForm = reactive({
    scheduledFor: '',
    placement: 'vitrine',
    notes: '',
  });
  const highlightSubmitting = ref(false);
  const showHighlightConfirm = ref(false);
  const highlightError = ref('');

  const highlightMinDateTime = computed(() => toDatetimeLocalValue(new Date()));

  const highlightSchedule = computed(
    () => unref(imovel)?.additionalAttributes?.highlightSchedule || null
  );
  const highlightProviderStatus = computed(
    () => highlightSchedule.value?.providerStatus || null
  );
  const highlightStatusMeta = computed(
    () => HIGHLIGHT_STATUS_META[highlightProviderStatus.value] || null
  );
  const highlightStatusTone = computed(
    () => highlightStatusMeta.value?.tone || 'neutral'
  );
  const highlightStatusLabel = computed(() => {
    switch (highlightProviderStatus.value) {
      case 'scheduled':
        return t('PROPERTIES.HIGHLIGHT.STATUS.SCHEDULED');
      case 'awaiting_provider_configuration':
        return t('PROPERTIES.HIGHLIGHT.STATUS.AWAITING');
      case 'published':
        return t('PROPERTIES.HIGHLIGHT.STATUS.PUBLISHED');
      case 'failed':
        return t('PROPERTIES.HIGHLIGHT.STATUS.FAILED');
      default:
        return t('PROPERTIES.HIGHLIGHT.STATUS.NONE');
    }
  });
  const highlightPlacementLabel = value => {
    switch (value) {
      case 'vitrine':
        return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.VITRINE');
      case 'destaque':
        return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.DESTAQUE');
      case 'super_destaque':
        return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.SUPER');
      default:
        return value;
    }
  };
  const highlightScheduledForLabel = computed(() =>
    highlightSchedule.value?.scheduledFor
      ? formatDateTime(highlightSchedule.value.scheduledFor)
      : null
  );
  const highlightProviderName = computed(
    () => highlightSchedule.value?.providerName || null
  );

  const isHighlightFormValid = computed(() => {
    if (!highlightForm.scheduledFor) return false;
    const date = new Date(highlightForm.scheduledFor);
    return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
  });
  const highlightConfirmDescription = computed(() =>
    t('PROPERTIES.HIGHLIGHT.CONFIRM.DESCRIPTION', {
      placement: highlightPlacementLabel(highlightForm.placement),
      date: highlightForm.scheduledFor
        ? formatDateTime(highlightForm.scheduledFor)
        : '',
    })
  );

  const openHighlightConfirm = () => {
    highlightError.value = '';
    if (!isHighlightFormValid.value) {
      highlightError.value = t('PROPERTIES.HIGHLIGHT.INVALID_DATE');
      return;
    }
    showHighlightConfirm.value = true;
  };

  const closeHighlightConfirm = () => {
    if (highlightSubmitting.value) return;
    showHighlightConfirm.value = false;
  };

  const submitHighlight = async () => {
    if (highlightSubmitting.value) return;
    if (!isHighlightFormValid.value) {
      highlightError.value = t('PROPERTIES.HIGHLIGHT.INVALID_DATE');
      showHighlightConfirm.value = false;
      return;
    }

    highlightSubmitting.value = true;
    highlightError.value = '';
    try {
      const { data } = await ImovelAPI.programarDestaque({
        imovel_ids: [unref(imovel).id],
        scheduled_for: new Date(highlightForm.scheduledFor).toISOString(),
        placement: highlightForm.placement,
        notes: highlightForm.notes.trim(),
      });
      showHighlightConfirm.value = false;
      await refreshImovel();
      trackListingAction('listing.highlight_scheduled', {
        placement: highlightForm.placement,
        scheduledCount: data?.scheduled,
        providerStatus: data?.provider_status,
      });
      useAlert(
        data?.provider_status === 'awaiting_provider_configuration'
          ? t('PROPERTIES.HIGHLIGHT.SUCCESS.AWAITING')
          : t('PROPERTIES.HIGHLIGHT.SUCCESS.SCHEDULED', {
              date: formatDateTime(data?.scheduled_for),
            })
      );
    } catch (error) {
      highlightError.value = actionErrorMessage(
        error,
        t('PROPERTIES.HIGHLIGHT.ERROR')
      );
      useAlert(highlightError.value);
    } finally {
      highlightSubmitting.value = false;
    }
  };

  return {
    canManageHighlight,
    highlightForm,
    highlightSubmitting,
    showHighlightConfirm,
    highlightError,
    highlightMinDateTime,
    highlightSchedule,
    highlightStatusTone,
    highlightStatusLabel,
    highlightPlacementLabel,
    highlightScheduledForLabel,
    highlightProviderName,
    isHighlightFormValid,
    highlightConfirmDescription,
    openHighlightConfirm,
    closeHighlightConfirm,
    submitHighlight,
  };
}

export default useImovelHighlight;
